import { ErrorModal } from "@dexteel/mesf-core";
import {
  Autocomplete,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import { get } from "lodash-es";
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Grade } from "@/shared/model/Grade";
import { getGrades } from "@/shared/repositories/GradeRepository";
import { getNearbyHeats } from "../repositories/ChemistriesReportRepository";
import { useUpdateChemistrySampleMutation } from "../hooks/mutations/useUpdateChemistrySampleMutation";

interface HeatOption {
  HeatId: number;
  HeatName: string;
}

interface EditSampleModalProps {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  sample: {
    ChemistrySampleId: number;
    HeatId: number;
    Heat: string;
    Grade: string;
  } | null;
}

export const EditSampleModal = ({
  open,
  onClose,
  onSaved,
  sample,
}: EditSampleModalProps) => {
  const [selectedHeat, setSelectedHeat] = useState<HeatOption | null>(null);
  const [selectedGrade, setSelectedGrade] = useState<Grade | null>(null);
  const [error, setError] = useState("");

  const { data: heats = [], isLoading: isLoadingHeats } = useQuery({
    queryKey: ["nearby-heats", sample?.Heat],
    queryFn: async () => {
      const resp = await getNearbyHeats(sample!.Heat);
      if (resp.ok) {
        return (resp.data?.tables?.[0]?.rows ?? []) as HeatOption[];
      }
      throw new Error(resp.message);
    },
    enabled: open && !!sample,
  });

  const { data: grades = [], isLoading: isLoadingGrades } = useQuery({
    queryKey: ["grades-enabled"],
    queryFn: async () => {
      const resp = await getGrades(false);
      if (resp.ok) {
        const gradeRows = (resp.data?.tables?.[0]?.rows ?? []) as Grade[];
        return gradeRows
          .filter((g) => g.Enabled)
          .sort((a, b) => a.GradeName.localeCompare(b.GradeName));
      }
      throw new Error(resp.message);
    },
    enabled: open,
  });

  const updateMutation = useUpdateChemistrySampleMutation();

  useEffect(() => {
    if (!open || !sample) return;
    setSelectedHeat(heats.find((h) => h.HeatId === sample.HeatId) ?? null);
    setSelectedGrade(grades.find((g) => g.GradeName === sample.Grade) ?? null);
  }, [open, sample, heats, grades]);

  const isLoading = isLoadingHeats || isLoadingGrades;

  const handleSave = async () => {
    if (!sample) return;

    const heatChanged = selectedHeat && selectedHeat.HeatId !== sample.HeatId;
    const gradeChanged =
      selectedGrade && selectedGrade.GradeName !== sample.Grade;

    try {
      await updateMutation.mutateAsync({
        chemistrySampleId: sample.ChemistrySampleId,
        heatName: heatChanged ? selectedHeat.HeatName : null,
        gradeId: gradeChanged ? selectedGrade.GradeId : null,
      });
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save changes");
    }
  };

  const hasChanges =
    (selectedHeat && selectedHeat.HeatId !== sample?.HeatId) ||
    (selectedGrade && selectedGrade.GradeName !== sample?.Grade);

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
        <DialogTitle>Edit Sample</DialogTitle>
        <DialogContent>
          {isLoading ? (
            <Grid container justifyContent="center" sx={{ py: 3 }}>
              <CircularProgress />
            </Grid>
          ) : (
            <Grid container spacing={2} sx={{ pt: 1 }}>
              <Grid item xs={12}>
                <Typography variant="body2" color="textSecondary">
                  Sample: {sample?.Heat} — {sample?.Grade}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Autocomplete
                  options={heats}
                  getOptionLabel={(opt) => opt.HeatName}
                  value={selectedHeat}
                  onChange={(_e, val) => setSelectedHeat(val)}
                  isOptionEqualToValue={(opt, val) => opt.HeatId === val.HeatId}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Heat"
                      variant="outlined"
                      size="small"
                      fullWidth
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Autocomplete
                  options={grades}
                  getOptionLabel={(opt) => opt.GradeName}
                  value={selectedGrade}
                  onChange={(_e, val) => setSelectedGrade(val)}
                  isOptionEqualToValue={(opt, val) =>
                    opt.GradeId === val.GradeId
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Grade"
                      variant="outlined"
                      size="small"
                      fullWidth
                    />
                  )}
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={updateMutation.isLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={updateMutation.isLoading || isLoading || !hasChanges}
          >
            {updateMutation.isLoading ? <CircularProgress size={20} /> : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
      <ErrorModal error={error} onHide={() => setError("")} />
    </>
  );
};
